/**
 * `<caos-rest-console>` — compose one request, read one response, four tabs around the pair.
 * (Catalog: Developer surfaces › RestConsole — compose a request and read its response, with headers and bodies as four tabs.)
 *
 * IT SENDS NOTHING. Pressing Send raises `caos-rest-send` carrying the composed request; the
 * application does the sending and hands back a response. That is not squeamishness about fetch —
 * it is that authentication, the base address, proxying and what counts as an error all belong to
 * the application, and a component that decided any of them would be usable by exactly one of them.
 *
 * FOUR TABS AROUND ONE REQUEST — request headers, request body, response body, response headers —
 * because all four are read while composing, and a console that hides the response headers behind a
 * toggle makes a person choose between seeing what they sent and seeing what came back.
 *
 * A METHOD THAT CARRIES NO BODY SAYS SO. GET and HEAD have no body, and the honest thing is to say
 * that in the body pane rather than either hiding the tab (so somebody thinks the console is broken)
 * or leaving an editable box whose contents are silently dropped (so somebody types a body and
 * cannot work out why it had no effect). The text is kept: switching to POST gets it back, because
 * choosing the wrong method first is the ordinary way this happens.
 *
 * THE STATUS IS NOT COLOUR ALONE. The response line reads the code, the reason, the timing and the
 * size as words. Colour repeats that; it never carries it.
 *
 * Styling seams: `::part(console)`, `::part(request-line)`, `::part(method)`, `::part(uri)`,
 * `::part(send)`, `::part(tabs)`, `::part(tab)`, `::part(pane)`, `::part(status)`,
 * `::part(headers)`, `::part(editor-surface)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { codeSurfaceEngine, CODE_SURFACE_CSS, type CodeSurfaceHandle } from '../code-surface-engine.js';

/** The methods the console offers, in the order it offers them. */
export const REST_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'] as const;
export type CaosRestMethod = (typeof REST_METHODS)[number];

/** The methods that carry no request body. See the header: this is stated, never silently applied. */
export const BODYLESS_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD']);

/** What came back. */
export interface CaosRestResponse {
  readonly status: number;
  /** The reason phrase, when the transport gives one. */
  readonly reason?: string;
  readonly headers?: Readonly<Record<string, string>>;
  readonly body?: string;
  /** How long it took, in milliseconds. */
  readonly tookMs?: number;
  /** How big the body is, in bytes. */
  readonly bytes?: number;
}

/** Which pane is showing. */
export type CaosRestPane = 'request-headers' | 'request-body' | 'response-body' | 'response-headers';

const PANE_LABELS: Readonly<Record<CaosRestPane, string>> = {
  'request-headers': 'Request headers',
  'request-body': 'Request body',
  'response-body': 'Response body',
  'response-headers': 'Response headers',
};

const sheet = makeSheet(`
  :host { display: block; }

  .console { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .request-line { display: flex; gap: var(--caos-space-2); align-items: stretch; flex-wrap: wrap; }

  .method, .uri {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font-mono);
    font-size: 12.5px;
    padding: 6px 9px;
  }
  .method { flex: 0 0 auto; font-weight: 600; }
  .uri { flex: 1 1 260px; min-width: 180px; }
  .method:focus-visible, .uri:focus-visible, .send:focus-visible, .tab:focus-visible {
    outline: none;
    box-shadow: var(--caos-focus-ring);
  }

  .send {
    flex: 0 0 auto;
    border: 1px solid var(--caos-color-accent);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    font-family: var(--caos-font);
    font-size: 13px;
    font-weight: 600;
    padding: 6px 16px;
    cursor: pointer;
  }
  .send:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }

  .tabs { display: flex; gap: var(--caos-space-1); flex-wrap: wrap; }

  .tab {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 10px;
    cursor: pointer;
  }
  .tab[aria-selected="true"] {
    color: var(--caos-color-text);
    border-color: var(--caos-color-accent);
    background: var(--caos-color-accent-soft);
  }

  .status {
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
  }
  .status strong { font-weight: 600; }
  .status[data-band="success"] strong { color: var(--caos-color-success-text); }
  .status[data-band="client"] strong { color: var(--caos-color-warning-text); }
  .status[data-band="server"] strong { color: var(--caos-color-danger-text); }

  .headers {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) auto;
    gap: var(--caos-space-2);
    align-items: center;
  }
  .headers input {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font-mono);
    font-size: 12px;
    padding: 4px 7px;
    min-width: 0;
  }
  .headers input:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .headers button {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: 12px;
    padding: 3px 8px;
    cursor: pointer;
  }

  .readonly-headers {
    font-family: var(--caos-font-mono);
    font-size: 12px;
    color: var(--caos-color-text);
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
  }

  .note {
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
    font-style: italic;
  }
`);

/** Which band a status code falls in. Used for the colour that REPEATS the words, never replaces them. */
export function statusBand(status: number): 'success' | 'redirect' | 'client' | 'server' | 'unknown' {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 300 && status < 400) return 'redirect';
  if (status >= 400 && status < 500) return 'client';
  if (status >= 500 && status < 600) return 'server';
  return 'unknown';
}

export class CaosRestConsole extends CaosElement {
  static get observedAttributes(): string[] {
    return ['method', 'uri', 'pane', 'sending', 'send-label'];
  }

  private methodEl: HTMLSelectElement;
  private uriEl: HTMLInputElement;
  private sendEl: HTMLButtonElement;
  private tabsEl: HTMLDivElement;
  private statusEl: HTMLParagraphElement;
  private paneEl: HTMLDivElement;

  private handle: CodeSurfaceHandle | null = null;
  private requestHeaders: Array<{ name: string; value: string }> = [];
  private bodyText = '';
  private result: CaosRestResponse | null = null;

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'console';
    wrap.setAttribute('part', 'console');

    const line = document.createElement('div');
    line.className = 'request-line';
    line.setAttribute('part', 'request-line');

    this.methodEl = document.createElement('select');
    this.methodEl.className = 'method';
    this.methodEl.setAttribute('part', 'method');
    this.methodEl.setAttribute('aria-label', 'Method');
    for (const m of REST_METHODS) {
      const o = document.createElement('option');
      o.value = m;
      o.textContent = m;
      this.methodEl.append(o);
    }
    this.methodEl.addEventListener('change', () => this.setAttribute('method', this.methodEl.value));

    this.uriEl = document.createElement('input');
    this.uriEl.type = 'text';
    this.uriEl.className = 'uri';
    this.uriEl.setAttribute('part', 'uri');
    this.uriEl.placeholder = '/services/data/v1/accounts';
    this.uriEl.setAttribute('aria-label', 'Address');
    this.uriEl.addEventListener('input', () => this.setAttribute('uri', this.uriEl.value));
    this.uriEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.send();
    });

    this.sendEl = document.createElement('button');
    this.sendEl.type = 'button';
    this.sendEl.className = 'send';
    this.sendEl.setAttribute('part', 'send');
    this.sendEl.addEventListener('click', () => this.send());

    line.append(this.methodEl, this.uriEl, this.sendEl);

    this.tabsEl = document.createElement('div');
    this.tabsEl.className = 'tabs';
    this.tabsEl.setAttribute('part', 'tabs');
    this.tabsEl.setAttribute('role', 'tablist');
    this.tabsEl.setAttribute('aria-label', 'Request and response');

    this.statusEl = document.createElement('p');
    this.statusEl.className = 'status';
    this.statusEl.setAttribute('part', 'status');
    this.statusEl.setAttribute('role', 'status');

    this.paneEl = document.createElement('div');
    this.paneEl.setAttribute('part', 'pane');

    wrap.append(line, this.statusEl, this.tabsEl, this.paneEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  disconnectedCallback(): void {
    this.handle?.destroy();
    this.handle = null;
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get method(): CaosRestMethod {
    const asked = (this.getAttribute('method') ?? '').toUpperCase();
    return (REST_METHODS as readonly string[]).includes(asked) ? (asked as CaosRestMethod) : 'GET';
  }

  get uri(): string {
    return this.getAttribute('uri') ?? '';
  }

  /** The request headers, as a list of pairs. Order is kept: a header list is not a set. */
  get headers(): ReadonlyArray<{ name: string; value: string }> {
    return this.requestHeaders.map((h) => ({ ...h }));
  }

  set headers(next: ReadonlyArray<{ name: string; value: string }>) {
    this.requestHeaders = Array.isArray(next) ? next.map((h) => ({ name: h.name, value: h.value })) : [];
    this.render();
  }

  /**
   * The request body text.
   *
   * Kept even for a method that carries no body. Choosing GET first and then meaning POST is the
   * ordinary way this happens, and throwing the text away on the method change punishes it.
   */
  get body(): string {
    return this.handle && this.pane === 'request-body' ? this.handle.getValue() : this.bodyText;
  }

  set body(text: string) {
    this.bodyText = text;
    if (this.pane === 'request-body') this.handle?.setValue(text);
  }

  get response(): CaosRestResponse | null {
    return this.result;
  }

  set response(next: CaosRestResponse | null) {
    this.result = next;
    // A response arriving is the moment to show it. Leaving the person on the request pane means
    // the thing they pressed Send for is behind a tab they have to know to press.
    if (next) this.setAttribute('pane', 'response-body');
    this.render();
  }

  get pane(): CaosRestPane {
    const asked = this.getAttribute('pane');
    return asked === 'request-body' || asked === 'response-body' || asked === 'response-headers'
      ? asked
      : 'request-headers';
  }

  /** Whether this method carries a body at all. */
  get carriesBody(): boolean {
    return !BODYLESS_METHODS.has(this.method);
  }

  /** The request as it stands — exactly what `caos-rest-send` carries. */
  get request(): { method: CaosRestMethod; uri: string; headers: Record<string, string>; body?: string } {
    const headers: Record<string, string> = {};
    for (const h of this.requestHeaders) if (h.name.trim() !== '') headers[h.name.trim()] = h.value;
    // A body is omitted for a method that has none, rather than sent as an empty string — an empty
    // body and no body are different requests, and some servers treat them differently.
    return this.carriesBody ? { method: this.method, uri: this.uri, headers, body: this.body } : { method: this.method, uri: this.uri, headers };
  }

  private send(): void {
    if (this.hasAttribute('sending') || this.uri.trim() === '') return;
    this.dispatchEvent(new CustomEvent('caos-rest-send', { bubbles: true, composed: true, detail: this.request }));
  }

  private setPane(pane: CaosRestPane): void {
    if (pane === this.pane) return;
    // Take the body back off the engine before it is torn down, or what was typed goes with it.
    if (this.pane === 'request-body' && this.handle) this.bodyText = this.handle.getValue();
    this.setAttribute('pane', pane);
  }

  private render(): void {
    this.methodEl.value = this.method;
    if (this.uriEl.value !== this.uri) this.uriEl.value = this.uri;
    const sending = this.hasAttribute('sending');
    this.sendEl.textContent = sending ? 'Sending…' : (this.getAttribute('send-label') ?? 'Send');
    this.sendEl.disabled = sending || this.uri.trim() === '';

    this.renderTabs();
    this.renderStatus();
    this.renderPane();
  }

  private renderTabs(): void {
    this.tabsEl.replaceChildren(
      ...(Object.keys(PANE_LABELS) as CaosRestPane[]).map((pane) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tab';
        b.setAttribute('part', 'tab');
        b.setAttribute('role', 'tab');
        const selected = pane === this.pane;
        b.setAttribute('aria-selected', selected ? 'true' : 'false');
        b.tabIndex = selected ? 0 : -1;
        b.textContent = PANE_LABELS[pane];
        b.addEventListener('click', () => this.setPane(pane));
        return b;
      }),
    );
  }

  private renderStatus(): void {
    if (!this.result) {
      this.statusEl.replaceChildren();
      this.statusEl.removeAttribute('data-band');
      return;
    }
    const { status, reason, tookMs, bytes } = this.result;
    const band = statusBand(status);
    this.statusEl.setAttribute('data-band', band);
    const strong = document.createElement('strong');
    // The words carry it. The colour only repeats them.
    strong.textContent = reason ? `${status} ${reason}` : String(status);
    const rest: string[] = [];
    if (tookMs !== undefined) rest.push(`${tookMs} ms`);
    if (bytes !== undefined) rest.push(`${bytes.toLocaleString()} bytes`);
    this.statusEl.replaceChildren(strong, ...(rest.length > 0 ? [document.createTextNode(` · ${rest.join(' · ')}`)] : []));
  }

  private renderPane(): void {
    this.handle?.destroy();
    this.handle = null;
    this.paneEl.replaceChildren();

    if (this.pane === 'request-headers') {
      this.paneEl.append(this.editableHeaders());
      return;
    }
    if (this.pane === 'response-headers') {
      this.paneEl.append(this.readOnlyHeaders(this.result?.headers));
      return;
    }
    if (this.pane === 'request-body' && !this.carriesBody) {
      const note = document.createElement('p');
      note.className = 'note';
      // Said, not hidden and not silently dropped. Hiding it reads as a broken console; leaving an
      // editable box that is thrown away leaves somebody unable to work out why their body did
      // nothing. The text is kept, so switching to POST gets it back.
      note.textContent = `A ${this.method} request carries no body. Anything typed here is kept and will be sent if the method changes.`;
      this.paneEl.append(note);
      return;
    }

    const isResponse = this.pane === 'response-body';
    if (isResponse && !this.result) {
      const note = document.createElement('p');
      note.className = 'note';
      note.textContent = 'Nothing sent yet.';
      this.paneEl.append(note);
      return;
    }

    const engine = codeSurfaceEngine();
    this.handle = engine.mount(this.paneEl, {
      value: isResponse ? (this.result?.body ?? '') : this.bodyText,
      language: 'json',
      readOnly: isResponse,
      label: isResponse ? 'Response body' : 'Request body',
      onChange: (value) => {
        this.bodyText = value;
      },
      onSubmit: () => this.send(),
    });
  }

  private editableHeaders(): HTMLElement {
    const grid = document.createElement('div');
    grid.className = 'headers';
    grid.setAttribute('part', 'headers');

    const rows = [...this.requestHeaders, { name: '', value: '' }];
    rows.forEach((header, i) => {
      const isBlank = i === this.requestHeaders.length;
      const name = document.createElement('input');
      name.type = 'text';
      name.value = header.name;
      name.placeholder = 'Header';
      name.setAttribute('aria-label', `Header ${i + 1} name`);
      const value = document.createElement('input');
      value.type = 'text';
      value.value = header.value;
      value.placeholder = 'Value';
      value.setAttribute('aria-label', `Header ${i + 1} value`);

      const commit = (): void => {
        if (isBlank) {
          if (name.value === '' && value.value === '') return;
          // The blank row at the bottom becomes a real one as soon as anything is typed into it, and
          // a new blank appears under it. No "add header" button to find.
          this.requestHeaders.push({ name: name.value, value: value.value });
          this.renderPane();
          return;
        }
        this.requestHeaders[i] = { name: name.value, value: value.value };
      };
      name.addEventListener('input', commit);
      value.addEventListener('input', commit);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove header ${header.name || i + 1}`);
      remove.hidden = isBlank;
      remove.addEventListener('click', () => {
        this.requestHeaders.splice(i, 1);
        this.renderPane();
      });

      grid.append(name, value, remove);
    });
    return grid;
  }

  private readOnlyHeaders(headers: Readonly<Record<string, string>> | undefined): HTMLElement {
    const pre = document.createElement('pre');
    pre.className = 'readonly-headers';
    const entries = Object.entries(headers ?? {});
    if (entries.length === 0) {
      pre.className = 'note';
      pre.textContent = this.result ? 'The response carried no headers.' : 'Nothing sent yet.';
      return pre;
    }
    pre.textContent = entries.map(([k, v]) => `${k}: ${v}`).join('\n');
    return pre;
  }
}

define('caos-rest-console', CaosRestConsole);

declare global {
  interface HTMLElementTagNameMap {
    'caos-rest-console': CaosRestConsole;
  }
}
